import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import LevelChip, { type Level } from '../../layout/context/LevelChip';
import RoleBadge from '../../layout/context/RoleBadge';
import { getRoleNames, type RoleName } from '../../../service/user.service';
import {
  addWorkspaceMember,
  removeWorkspaceMember,
  updateWorkspaceMemberRole
} from '../../../service/workspaceMembers.service';
import { messageFor } from '../../../service/mfa.service';
import { MEMBERSHIP_ERRORS } from '../../../service/userLifecycle.service';
import { deleteUsers } from '../../../service/user.service';
import { useUserAdministration } from '../../hooks/useUserAdministration';
import {
  confirmRemoveFromScope,
  confirmResetFactor,
  confirmSendPasswordReset,
  confirmToggleActive
} from './memberActions';
import { grantExceedsFrom403 } from '../../../service/capability.service';
import { moduleLabel } from '../../../config/capabilities';
import { 
  RoleSelect, 
  roleKey, 
  targetLabel, 
  type InviteTarget 
} from '../invitations/InviteModal';

export interface MemberMembership {
  node_id: number;
  node_kind: string;
  node_name: string;
  role: string | null;
  custom_role_id?: number | null;
}

export interface MemberPerson {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  lastLoginAt?: string | null;
  /** `undefined` = not reported (unknown), never "off". */
  totpEnabled?: boolean;
  /** Keycloak `emailVerified`; `undefined` = not reported. */
  emailVerified?: boolean;
  memberships: MemberMembership[];
}

export type MemberDialogTab = 'access' | 'security' | 'danger';

interface MemberDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after any change so the caller refreshes its list. */
  onChanged: () => void;
  person: MemberPerson | null;
  /** Nodes this console manages; memberships elsewhere are shown but not editable. */
  scopeNodeIds: number[];
  readOnly?: boolean;
  tabs?: MemberDialogTab[];
  /** Name of the console node, used in "Remove from ...". */
  scopeLabel?: string;
  /** Node the console runs on; the caller's own tier there decides what may be changed. */
  adminNodeId?: number;
  /** Places an administrator may add access on; ones the person already holds are left out. */
  targets?: InviteTarget[];
  /** Rendered under the Access table (e.g. effective access details). */
  children?: React.ReactNode;
}

const bodyFor = (key: string) => key.startsWith('c:')
  ? { custom_role_id: Number(key.slice(2)) }
  : { role: key };

const failureText = (error: any, fallback: string): string => {
  const modules = grantExceedsFrom403(error);
  return modules
    ? `You cannot grant more than you hold. Exceeds your access on: ${modules.map(moduleLabel).join(', ') || 'unknown modules'}.`
    : messageFor(error, MEMBERSHIP_ERRORS, fallback);
};

const fullName = (person: MemberPerson) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email;

/** The membership's role as the options list keys it: by id for a custom role, else by name. */
const currentKey = (membership: MemberMembership, roles: RoleName[]): string | undefined => {
  if (membership.custom_role_id != null) return `c:${membership.custom_role_id}`;
  const match = roles.find((role) => role.custom_role_id == null && role.name === membership.role)
    ?? roles.find((role) => role.name === membership.role);
  return match ? roleKey(match) : membership.role ?? undefined;
};

/** Inline role editor for one membership; loads the server's own list for that node. */
const MembershipRole: React.FC<{
  membership: MemberMembership;
  personId: string;
  onChanged: () => void;
}> = ({ membership, personId, onChanged }) => {
  const [roles, setRoles] = useState<RoleName[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState<string | undefined>();

  useEffect(() => {
    let stale = false;
    setLoading(true);
    getRoleNames(membership.node_id)
      .then((list) => {
        if (stale) return;
        const assignable = list.filter((role) => role.assignable !== false);
        setRoles(assignable);
        setValue(currentKey(membership, assignable));
      })
      .catch(() => !stale && setRoles([]))
      .finally(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership.node_id, membership.role, membership.custom_role_id]);

  const apply = async (key: string) => {
    await updateWorkspaceMemberRole(membership.node_id, personId, bodyFor(key));
  };

  const change = async (next: string) => {
    const previous = value;
    const nextName = roles.find((role) => roleKey(role) === next)?.name ?? next;
    setSaving(true);
    try {
      await apply(next);
      setValue(next);
      const hide = message.open({
        type: 'success',
        duration: 10,
        content: (
          <span>
            Role changed to {nextName}{' '}
            {previous && (
              <a
                onClick={async () => {
                  hide();
                  try {
                    await apply(previous);
                    setValue(previous);
                    message.success('Role change undone.');
                    onChanged();
                  } catch (error: any) {
                    message.error(failureText(error, 'The role could not be restored.'));
                  }
                }}
              >Undo</a>
            )}
          </span>
        )
      });
      onChanged();
    } catch (error: any) {
      message.error(failureText(error, 'The role could not be changed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select
      size="small"
      style={{ minWidth: 150 }}
      loading={loading || saving}
      disabled={saving}
      value={value}
      onChange={change}
      optionLabelProp="short"
      options={roles.map((role) => ({
        value: roleKey(role),
        short: role.name,
        label: (
          <div>
            <div>{role.name}</div>
            {role.description && (
              <div 
                className="text-gray-400 text-xs whitespace-normal"
              >{role.description}</div>
            )}
          </div>
        )
      }))}
    />
  );
};

const MemberDialog: React.FC<MemberDialogProps> = ({
  open,
  onClose,
  onChanged,
  person,
  scopeNodeIds,
  readOnly = false,
  tabs = ['access'],
  scopeLabel,
  children,
  adminNodeId,
  targets
}) => {
  const { administrabilityOf, canManageAccounts } = useUserAdministration(adminNodeId);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addNode, setAddNode] = useState<number | undefined>();
  const [addRole, setAddRole] = useState<string | undefined>();
  const [addTried, setAddTried] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | undefined>();
  if (!person) return null;
  const scopeName = scopeLabel
    || person.memberships.filter((m) => scopeNodeIds.includes(m.node_id)).map((m) => m.node_name).join(', ')
    || 'this node';
  const { allowed, reason } = administrabilityOf({
    email: person.email,
    roles: person.memberships.map((m) => m.role ?? '')
  });
  const inScope = (membership: MemberMembership) => scopeNodeIds.includes(membership.node_id);

  const remove = async (membership: MemberMembership) => {
    try {
      await removeWorkspaceMember(membership.node_id, person.id);
      message.success(`${fullName(person)} removed from ${membership.node_name}.`);
      onChanged();
      if (person.memberships.length <= 1) onClose();
    } catch (error: any) {
      message.error(failureText(error, 'The member could not be removed.'));
    }
  };

  const heldNodes = new Set(person.memberships.map((m) => m.node_id));
  const addable = (targets ?? []).filter((target) => !heldNodes.has(target.id));

  const closeAdd = () => {
    setAdding(false);
    setAddNode(undefined);
    setAddRole(undefined);
    setAddTried(false);
    setAddError(undefined);
  };

  const loadRoles = (nodeId: number) =>
    getRoleNames(nodeId)
      .then((list) => list.filter((role) => role.assignable !== false))
      .catch(() => [] as RoleName[]);

  const runAdd = async () => {
    setAddTried(true);
    setAddError(undefined);
    if (addNode == null || !addRole) return;
    setAddBusy(true);
    try {
      await addWorkspaceMember(addNode, { 
        user_id: person.id, 
        ...bodyFor(addRole) 
      });
      const nodeName = addable.find((target) => target.id === addNode)?.name ?? 'the selected place';
      const roleName = addRole.startsWith('c:') ? 'custom role' : addRole;
      message.success(`Access added: ${roleName} on ${nodeName}`);
      onChanged();
      closeAdd();
    } catch (error: any) {
      setAddError(failureText(error, 'The access could not be added.'));
    } finally {
      setAddBusy(false);
    }
  };

  const addAccess = !readOnly && allowed && addable.length > 0 && (
    adding ? (
      <div>
        {addError && <Alert 
          type="error" 
          showIcon 
          message={addError} 
          className="mb-2" /
        >}
        <div 
          className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] items-start gap-2"
        >
          <div>
            <div className="text-xs text-gray-500">Where</div>
            <Select
              className="w-full"
              placeholder="Organisation, entity or workspace"
              value={addNode}
              status={addTried && addNode == null ? 'error' : undefined}
              onChange={(value) => setAddNode(value)}
              options={addable.map((target) => ({ 
                value: target.id, 
                label: targetLabel(target) 
              }))}
            />
            {addTried && addNode == null && <Typography.Text 
              type="danger" 
              className="text-xs"
            >Select where</Typography.Text>}
          </div>
          <div>
            <div className="text-xs text-gray-500">Role</div>
            <RoleSelect
              nodeId={addNode}
              value={addRole}
              onChange={setAddRole}
              loadRoles={loadRoles}
            />
            {addTried && !addRole && <Typography.Text 
              type="danger" 
              className="text-xs"
            >Select a role</Typography.Text>}
          </div>
          <Space className="pt-[18px]">
            <Button 
              type="primary" 
              loading={addBusy} 
              onClick={runAdd}
            >Add</Button>
            <Button onClick={closeAdd}>Cancel</Button>
          </Space>
        </div>
      </div>
    ) : (
      <Button 
        type="dashed" 
        icon={<PlusOutlined />} 
        block 
        onClick={() => setAdding(true)}
      >Add access</Button>
    )
  );

  const accessTab = (
    <Space direction="vertical" size="large" className="w-full">
      {!allowed && !readOnly && <Typography.Text 
        type="secondary"
      >{reason}</Typography.Text>}
      <Table
        size="small"
        pagination={false}
        rowKey={(membership: MemberMembership) => `${membership.node_id}`}
        dataSource={person.memberships}
        columns={[
          {
            title: 'Where',
            render: (_: unknown, membership: MemberMembership) => (
              <span
                className="flex items-center gap-2"
                style={inScope(membership) ? undefined : { opacity: 0.5 }}
              >
                <LevelChip level={membership.node_kind as Level} compact />
                <span>{membership.node_name}</span>
              </span>
            )
          }, {
            title: 'Role',
            render: (_: unknown, membership: MemberMembership) => {
              if (!inScope(membership)) {
                return (
                  <span 
                    className="flex items-center gap-2" 
                    style={{ opacity: 0.5 }}
                  >
                    <span>{membership.role ?? 'custom'}</span>
                    <Tag>Managed elsewhere</Tag>
                  </span>
                );
              }
              return readOnly || !allowed
                ? <RoleBadge role={membership.role} />
                : <MembershipRole 
                  membership={membership} 
                  personId={person.id} 
                  onChanged={onChanged} /
                >;
            }
          }, {
            title: '',
            width: 48,
            render: (_: unknown, membership: MemberMembership) => (
              inScope(membership) && !readOnly && allowed ? (
                <Popconfirm
                  title={`Remove ${fullName(person)} from ${membership.node_name}?`}
                  description="Keeps their account and any other access."
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => remove(membership)}
                >
                  <Tooltip title={`Remove from ${membership.node_name}`}>
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      aria-label={`Remove from ${membership.node_name}`}
                    />
                  </Tooltip>
                </Popconfirm>
              ) : null
            )
          }
        ]}
      />
      {addAccess}
      {children}
    </Space>
  );

  const factorRow = (title: string, state: boolean | undefined, method: 'totp') => (
    <div className="flex items-center justify-between py-2">
      <span>
        <strong>{title}</strong>
        <br />
        <Typography.Text type="secondary" className="text-xs">
          {state === undefined ? 'Not reported' : state ? 'Set up' : 'Not set up'}
        </Typography.Text>
      </span>
      {state !== false && (
        <Button
          type="primary"
          onClick={() => confirmResetFactor(person, method, onChanged)}
        >Reset</Button>
      )}
    </div>
  );

  const securityTab = (
    <div>
      {factorRow('Authenticator app', person.totpEnabled, 'totp')}
      <div className="py-2">
        <strong>Email verified</strong>
        <br />
        <Typography.Text type="secondary" className="text-xs">
          {person.emailVerified === undefined
            ? 'Not reported'
            : person.emailVerified ? 'Verified' : 'Not verified yet'}
        </Typography.Text>
      </div>
      <div className="py-2">
        <div className="flex items-center justify-between">
          <strong>Password</strong>
          <Button 
            type="primary" 
            onClick={() => confirmSendPasswordReset(person)}
          >
            Send password reset email
          </Button>
        </div>
        <Typography.Text type="secondary" className="text-xs">
          Passwords and second factors are managed by the identity provider; you can reset them, never see or set them.
        </Typography.Text>
      </div>
    </div>
  );

  const dangerRow = (title: string, text: string, label: string, onClick: () => void) => (
    <div className="flex items-center justify-between gap-4 py-2">
      <span>
        <strong>{title}</strong>
        <br />
        <Typography.Text type="secondary">{text}</Typography.Text>
      </span>
      <Button type="primary" danger onClick={onClick}>{label}</Button>
    </div>
  );

  const confirmDelete = () => {
    setTyped('');
    setDeleteError(undefined);
    setDeleteOpen(true);
  };

  const runDelete = async () => {
    if (typed.trim().toLowerCase() !== person.email.toLowerCase()) {
      setDeleteError('That does not match the account email.');
      return;
    }
    setDeleting(true);
    try {
      await deleteUsers([person.id]);
      message.success(`${person.email} deleted.`);
      setDeleteOpen(false);
      onChanged();
      onClose();
    } catch (error: any) {
      setDeleteError(messageFor(error, MEMBERSHIP_ERRORS, 'The account could not be deleted.'));
    } finally {
      setDeleting(false);
    }
  };

  const dangerTab = (
    <div className="border border-red-300 rounded p-4">
      {dangerRow(
        `Remove from ${scopeName}`,
        'Removes every role they hold here and below. Keeps their account.',
        'Remove',
        () => confirmRemoveFromScope(person, scopeNodeIds, scopeName, () => {
          onChanged();
          onClose();
        }, onChanged)
      )}
      {dangerRow(
        person.enabled ? 'Suspend account' : 'Reactivate account',
        person.enabled ? 'Blocks sign-in. Reversible.' : 'Lets them sign in again.',
        person.enabled ? 'Suspend account' : 'Reactivate account',
        () => confirmToggleActive(person, () => {
          onChanged();
          onClose();
        })
      )}
      {canManageAccounts && dangerRow(
        'Delete account',
        'Removes the account permanently.',
        'Delete account',
        confirmDelete
      )}
    </div>
  );

  const restricted = (content: React.ReactNode) =>
    allowed ? content : <Typography.Text 
      type="secondary"
    >{reason}</Typography.Text>;

  const items = tabs.map((tab) => ({
    key: tab,
    label: { 
      access: 'Access', 
      security: 'Security', 
      danger: 'Danger zone' 
    }[tab],
    children: tab === 'access'
      ? accessTab
      : restricted(tab === 'security' ? securityTab : dangerTab)
  }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      centered
      destroyOnClose
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      title={
        <Space>
          <Avatar>{(person.firstName || person.email)[0]?.toUpperCase()}</Avatar>
          <span>
            <strong>{fullName(person)}</strong>{' '}
            <Tag 
              color={person.enabled ? 'green' : 'red'}
            >{person.enabled ? 'Active' : 'Suspended'}</Tag>
            <br />
            <Typography.Text 
              type="secondary" 
              className="text-xs"
            >{person.email}</Typography.Text>
          </span>
        </Space>
      }
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <Tabs items={items} />
      <Modal
        open={deleteOpen}
        centered
        title={`Delete ${person.email}?`}
        okText="Delete account"
        okButtonProps={{ danger: true }}
        confirmLoading={deleting}
        onOk={runDelete}
        onCancel={() => setDeleteOpen(false)}
      >
        <p>
          This removes the account permanently. Type <strong>{person.email}</strong> to confirm.
        </p>
        <Input
          autoFocus
          value={typed}
          status={deleteError ? 'error' : undefined}
          onChange={(event) => {
            setTyped(event.target.value);
            setDeleteError(undefined);
          }}
          onPressEnter={runDelete}
        />
        {deleteError && <Typography.Text 
          type="danger"
        >{deleteError}</Typography.Text>}
      </Modal>
    </Modal>
  );
};

export default MemberDialog;
